import { Injectable } from '@angular/core'
import { ActivatedRouteSnapshot, Event, NavigationEnd, Router } from '@angular/router'
import { AuthEventType } from '@ftr/api-shared'
import { Uuid } from '@ftr/contracts/type/shared'
import { ArrayUtils, SimpleWindowRefService, TrackingEventType, TrackingService, isInDesktopApp } from '@ftr/foundation'
import { FeatureFlagState } from '@ftr/ui-feature-flags'
import { Store } from '@ngxs/store'
import { isEqual } from 'lodash-es'
import { combineLatest, distinctUntilChanged, filter, map } from 'rxjs'
import { UserState } from '../../store'
import { AuthenticationService } from '../authentication.service'
import { AmplitudeService } from './amplitude.service'
import { AnalyticsEvent } from './analytics-event'

export const EMPTY_TRACKING_VALUE = 'EMPTY'

const PAGES_EXCLUDED_FROM_GA_EVENTS = ['resetverify']

/**
 * Implementation of tracking service setup to work with GTM and GA4.
 * It's recommended to depend on TrackingService throughout the application rather than this class.
 *
 * GTM documentation:
 * https://www.notion.so/ftrlabs/Analytics-and-Tracking-293e8c890b5347278a15c77d4af38a8f
 */
@Injectable({
  providedIn: 'root',
})
export class AnalyticsService extends TrackingService {
  constructor(
    private readonly windowRefService: SimpleWindowRefService,
    private readonly authenticationService: AuthenticationService,
    private readonly router: Router,
    private readonly store: Store,
    private readonly amplitudeService: AmplitudeService,
  ) {
    super()
  }

  track(data: AnalyticsEvent): void {
    this.windowRefService.dataLayer().push(data)
  }

  /**
   * To avoid duplicate events should only be called once on app init
   */
  observePageViewsAndAuthEvents(): void {
    this.watchAuthenticationEvents()
    this.watchPageViews()
  }

  // On init it should try and register the user as there may be no authEvents firing.
  register(): void {
    this.registerUser()
    this.assignCourtSystem()
    this.assignUserGroups()
    this.watchAmplitudeTracking()
  }

  private assignCourtSystem(): void {
    this.store
      .select(UserState.currentCourtSystem)
      .pipe(distinctUntilChanged())
      .subscribe(currentCourtSystem => {
        if (currentCourtSystem) {
          this.track({ event: TrackingEventType.UserParameterUpdate, courtSystemName: currentCourtSystem.name })
        } else {
          this.track({ event: TrackingEventType.UserParameterUpdate, courtSystemName: EMPTY_TRACKING_VALUE })
        }
      })
  }

  private assignUserGroups(): void {
    // For the purposes of analytics, we only want to send
    // the groups for the court system the user has selected in the header.
    // Filter out the default case of UserState.groups (which is undefined, as no groups is [])
    this.store
      .select(UserState.currentUserGroups)
      .pipe(
        filter(r => r.isSuccess() && !!r.data),
        map(r => r.data!.map(u => u.name)),
        distinctUntilChanged(ArrayUtils.shallowEquals),
      )
      .subscribe(userGroups => {
        if (userGroups && userGroups.length) {
          this.track({ event: TrackingEventType.UserParameterUpdate, courtSystemUserRoles: userGroups.join(';') })
        } else {
          this.track({ event: TrackingEventType.UserParameterUpdate, courtSystemUserRoles: EMPTY_TRACKING_VALUE })
        }
      })
  }

  private watchAuthenticationEvents(): void {
    this.authenticationService.authEvents.subscribe(authEvent => {
      if (authEvent.type === AuthEventType.Logout) {
        this.logOutUser()
      } else {
        this.registerUser()
      }
    })
  }

  private watchPageViews(): void {
    this.router.events.subscribe((event: Event) => {
      if (event instanceof NavigationEnd) {
        const urlPath = getAbstractRoutePath(this.router.routerState.snapshot)
        this.sendPage(event.url, event.urlAfterRedirects, urlPath)
      }
    })
  }

  /**
   * The court system and the flag decide if Amplitude hears about this session. AmplitudeService
   * then applies the region rule, which stays there so that no caller can go around it.
   */
  private watchAmplitudeTracking(): void {
    combineLatest([
      this.store.select(UserState.currentCourtSystem),
      this.store.select(FeatureFlagState.featureFlag('amplitude-analytics')),
    ])
      .pipe(distinctUntilChanged((a, b) => isEqual(a, b)))
      .subscribe(([currentCourtSystem, amplitudeAnalyticsEnabled]) => {
        if (currentCourtSystem && amplitudeAnalyticsEnabled) {
          this.amplitudeService.setCourtSystem(currentCourtSystem)
        } else {
          this.amplitudeService.clearCourtSystem()
        }
      })
  }

  private registerUser(): void {
    const user = this.authenticationService.currentUser
    if (user) {
      // So we can segment automated test users out of GA.
      const automatedTestUser = /(^ftr-?\.?|@mailinator.com|@mailosaur.io$)/.test(user.email)
      const userEmailHostName = user.email.split('@')[1]
      const isInternalFtrUser = userEmailHostName.toLowerCase() === 'fortherecord.com'
      const userIsInDesktopApp = isInDesktopApp()

      this.sendUser(user.id, automatedTestUser, userEmailHostName, userIsInDesktopApp, isInternalFtrUser)

      this.amplitudeService.setUser({
        userId: user.id,
        isAutomatedTestUser: automatedTestUser,
        userEmailHostName,
        userIsInDesktopApp,
        isInternalFtrUser,
      })
    }
  }

  /**
   * Google's policy requires us to unset when the user logs out
   * @see https://support.google.com/tagmanager/answer/4565987?hl=en
   */
  private logOutUser(): void {
    this.sendUser(null, undefined, undefined, undefined, undefined)
    this.amplitudeService.clearUser()
  }

  /**
   * GA has built-in userId tracking to link users across devices.
   * Access the user id view in GA by clicking "All Web Site Data" and in the Views pane, User ID view
   */
  private sendUser(
    userId: Uuid | null,
    isAutomatedTestUser: boolean | undefined,
    userEmailHostName: string | undefined,
    userIsInDesktopApp: boolean | undefined,
    isInternalFtrUser: boolean | undefined,
  ): void {
    this.track({
      event: TrackingEventType.UserParameterUpdate,
      userId,
      isAutomatedTestUser,
      userEmailHostName,
      userIsInDesktopApp,
      isInternalFtrUser,
    })
  }

  /**
   * PageView is a Custom Event configured in GTM's Triggers
   */
  private sendPage(previousUrl: string, currentUrl: string, urlPath: string): void {
    if (shouldSendPageView(previousUrl, currentUrl)) {
      this.track({ event: TrackingEventType.PageView, url: currentUrl, urlPath })
    }
  }
}

function shouldSendPageView(previousUrl: string, currentUrl: string): boolean {
  return (
    !PAGES_EXCLUDED_FROM_GA_EVENTS.some(urlToMatch => matchUrl(previousUrl, urlToMatch)) &&
    !PAGES_EXCLUDED_FROM_GA_EVENTS.some(urlToMatch => matchUrl(currentUrl, urlToMatch))
  )
}

function matchUrl(url: string, urlToMatch: string): boolean {
  const regex = new RegExp(`/${urlToMatch}[/?]?.*`)
  return regex.test(url)
}

/**
 * Resolves the abstract route path from the router state snapshot, using param placeholders
 * instead of resolved IDs. E.g. /court-system/:courtSystemId/recording-playback/stream/:streamId.
 * This keeps GUIDs out of analytics while preserving meaningful route names.
 */
function getAbstractRoutePath(snapshot: { root: ActivatedRouteSnapshot }): string {
  let route = snapshot.root
  while (route.firstChild) route = route.firstChild

  return (
    '/' +
    route.pathFromRoot
      .map(r => r.routeConfig?.path)
      .filter(Boolean)
      .join('/')
  )
}
